import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Button } from "@mui/material";
import CustomActionPanel from "../actions/CustomActionPanel";
import DynamicFilter from "./DynamicFilter";
import FilterTags from "./FilterTags";
import { extractProperty, normalizeValues } from "../../utils/commonUtils";
import { useResponsiveStyles } from "../../styles/useResponsiveStyles";

const FilterPanel = ({
    filtersContent,
    onApply,
    handlePanelClose,
    appliedFilters,
    isFilterableContentPanel,
}) => {
    const [updatedFiltersContent, setUpdateFiltersContent] = useState([]);
    const [selectedFilters, setSelectedFilters] = useState({});
    const [rangeFilterKeys, setRangeFilterKeys] = useState([]);
    const [selectedFiltersArray, setSelectedFiltersArray] = useState([]);
    const { getContainerWidth, getFontSize, getButtonStyle } =
        useResponsiveStyles();
    const { defaultFilterValues } = useSelector((state) => state.filters);
    // Handles the addition or change of a filter

    useEffect(() => {
        const formattedFilters = {};
        const rangeFilterCpy = [];
        const updatedFiltersContent = [];
        filtersContent.forEach((filter) => {
            const key = filter.key;
            if (
                filter.type === "range-slider" &&
                !rangeFilterCpy.includes(key)
            ) {
                rangeFilterCpy.push(key);
            }
            if (appliedFilters && appliedFilters[key]) {
                formattedFilters[key] = appliedFilters[key];
            }
            if (defaultFilterValues[key]) {
                filter.defaultFilterRange = defaultFilterValues[key];
            }
            updatedFiltersContent.push(filter);
        });
        setRangeFilterKeys(rangeFilterCpy);
        setUpdateFiltersContent(updatedFiltersContent);
        setSelectedFilters(formattedFilters || {});
    }, [appliedFilters, filtersContent]);

    const handleFilterChange = (filterName, value) => {
        // console.log(filterName, value, "Range");
        setSelectedFilters((prevFilters) => ({
            ...prevFilters,
            [filterName]: value,
        }));
    };

    // Handles removing a specific filter value
    const handleFilterRemove = (filterName, filterValue) => {
        setSelectedFilters((prevFilters) => {
            const prevFiltersCpy = { ...prevFilters };
            const filterVal = prevFiltersCpy[filterName];
            if (rangeFilterKeys.includes(filterName)) {
                delete prevFiltersCpy[filterName];
            } else if (Array.isArray(filterVal)) {
                // Remove the specific value from an array of filter values
                prevFiltersCpy[filterName] = filterVal.filter((val) => {
                    return (
                        extractProperty(val, "label") !==
                        extractProperty(filterValue, "label")
                    );
                });
                // If the array is empty, delete the filter
                if (prevFiltersCpy[filterName].length === 0) {
                    delete prevFiltersCpy[filterName];
                }
            } else {
                // Remove the entire filter if it's not an array
                delete prevFiltersCpy[filterName];
            }
            return prevFiltersCpy;
        });
    };

    // Clears all selected filters
    const onClearAll = () => {
        setSelectedFilters({});
    };

    // Prepares the filters for submission by extracting the IDs
    const applyFilters = () => {
        const extractedFilterValues = Object.entries(selectedFilters).reduce(
            (acc, [category, values]) => {
                acc[category] = normalizeValues(values, "id");
                return acc;
            },
            {}
        );

        // Calculate actual filter count based on selected values
        const actualFilterCount = Object.values(selectedFilters).reduce(
            (count, values) => {
                return (
                    count +
                    (Array.isArray(values) ? values.length : values ? 1 : 0)
                );
            },
            0
        );

        onApply(extractedFilterValues, actualFilterCount, selectedFilters);
    };

    useEffect(() => {
        const selectedFiltersArray = Object.entries(selectedFilters)
            .filter(([key, value]) => !rangeFilterKeys.includes(key))
            .flatMap(([name, value]) => {
                return normalizeValues(value, "label").map((val) => ({
                    name,
                    value: val,
                }));
            });
        rangeFilterKeys.forEach((key) => {
            if (selectedFilters[key]) {
                const value = {
                    value: selectedFilters[key].join("-"),
                    name: key,
                };
                selectedFiltersArray.push(value);
            }
        });
        setSelectedFiltersArray(selectedFiltersArray);
    }, [selectedFilters]);
    // Converts selected filters into an array format for display

    return (
        <Box
            sx={{
                ...getContainerWidth("filter-panel"),
                ...styles.filterBox,
            }}
        >
            <CustomActionPanel
                header={`Filters${
                    selectedFiltersArray.length
                        ? ` (${selectedFiltersArray.length})`
                        : ""
                }`}
                onClose={handlePanelClose}
                sx={styles.actionPanel}
                removePadding={isFilterableContentPanel}
            >
                <FilterTags
                    initialFilters={selectedFiltersArray}
                    onFilterRemove={handleFilterRemove}
                />
                <DynamicFilter
                    filters={updatedFiltersContent}
                    selectedFilterProps={selectedFilters}
                    handleFilterChange={handleFilterChange}
                />
                <Box sx={styles.buttonGroup}>
                    <Button
                        variant="outlined"
                        sx={{
                            ...styles.clearAllBtn,
                            ...getFontSize("body1"),
                            ...getButtonStyle("filter-action-button"),
                        }}
                        onClick={onClearAll}
                        color="primary"
                    >
                        Clear All
                    </Button>
                    <Button
                        variant="contained"
                        sx={{
                            ...styles.filterBtn,
                            ...getFontSize("body1"),
                            ...getButtonStyle("filter-action-button"),
                        }}
                        onClick={applyFilters}
                        color="primary"
                    >
                        Apply
                    </Button>
                </Box>
            </CustomActionPanel>
        </Box>
    );
};

const styles = {
    filterBox: {
        pr: "6px",
        pt: "6px",
    },
    actionPanel: {
        height: "100%",
        bgcolor: "#FFFFFF",
        color: "#C43A29",
        m: "6px",
    },
    buttonGroup: {
        display: "flex",
        justifyContent: "space-between",
        paddingBottom: 4,
    },
    filterBtn: {
        background: "linear-gradient(92.79deg, #F7454C 3%, #ED1C24 100%)",
        color: "white",
        fontWeight: "bold",
        borderRadius: "8px",
        letterSpacing: "0.4px",
        whiteSpace: "nowrap",
        minWidth: "100px",
        px: 3,
    },
    clearAllBtn: {
        borderColor: "#939598",
        borderWidth: "1px",
        fontWeight: "bold",
        backgroundColor: "#f5f5f5",
        color: "#939598",
        borderRadius: "8px",
        letterSpacing: "0.4px",
        whiteSpace: "nowrap",
        minWidth: "100px",
        px: 3,
    },
};

export default FilterPanel;
